import {
  useEffect, useRef, useState,
} from 'react';
import { useRelayEnvironment } from 'react-relay';

import FlashesStore from 'src/stores/FlashesStore';
import logger from 'src/util/logger';

import { getPropertyDataInBatches, getTimeSeriesData } from '../helpers/dataBatching';
import { PropertyShowLoadingActions } from '../reducer/constants';

/**
 * Custom hook that encapsulates all property data fetching.
 *
 * Handles:
 * - Batched data loading with progress tracking via reducer dispatch
 * - Data transformation from GraphQL response to meter objects
 * - Error handling and user feedback
 *
 * Loading state is managed externally via the stateDispatchFunc reducer.
 * @param {object} params - Hook parameters
 * @param {string} params.propertyId - The property UUID to fetch data for
 * @param {object} params.dateSpan - Object with start and finish Luxon DateTime objects
 * @param {string} params.timezone - Timezone of the property
 * @param {object} params.initialData - Static/initial property data
 * @param {string} params.historianAggregation - Aggregation level
 * for historian data (e.g., 'PT30M', 'P1D')
 * @param {string} params.tradeAggregation - Aggregation level for trade data
 * @param {Function} [params.stateDispatchFunc] - Dispatch function for loading state reducer
 * @returns {object} Object containing:
 *   - combinedData: {object} Combined property data from GraphQL responses
 */
const usePropertyData = ({
  propertyId,
  dateSpan,
  timezone,
  initialData,
  historianAggregation,
  tradeAggregation,
  stateDispatchFunc,
}) => {
  const environment = useRelayEnvironment();

  const [combinedData, setCombinedData] = useState(null);

  // Cache static data (meters, rules, property info) to avoid refetching on dateSpan change
  const staticDataCache = useRef({
    meters: null,
    rulesData: null,
    hasStaticData: false,
  });

  // Track if this is the initial load (first effect is handling it)
  const isInitialLoadRef = useRef(true);

  // Track if we're currently fetching data to prevent parallel fetches
  const isFetchingData = useRef(false);

  // First effect: Fetch static data (meters, rules) once per propertyId
  useEffect(() => {
    if (!propertyId) {
      logger.error('No property ID found for property data fetching');
      return;
    }

    // Only fetch if we don't have cached static data
    if (staticDataCache.current.hasStaticData) {
      logger.log('Using cached static data (meters, rules)');
      return;
    }

    logger.log('Fetching static property data (meters, rules) for property:', propertyId);

    if (stateDispatchFunc) {
      stateDispatchFunc({ type: PropertyShowLoadingActions.START_REQUEST });
    }

    getPropertyDataInBatches({
      propertyId,
      dateSpan,
      timezone,
      environment,
      historianAggregation,
      tradeAggregation,
      initialData,
      isFetchingRef: isFetchingData,
      onDataReceived: (mergedData) => {
        logger.log('Received merged property data with property:', !!mergedData?.property);
        // Cache the static data for future dateSpan changes
        if (mergedData?.property?.meters?.edges) {
          const meters = mergedData.property.meters.edges.map((edge) => edge.node);
          const rulesData = meters.map((meter) => ({
            uuid: meter.uuid,
            rules: meter.rules,
          }));
          staticDataCache.current = {
            meters,
            rulesData,
            hasStaticData: true,
          };
          logger.log('Cached static data:', meters.length, 'meters');
        }
        // Mark initial load as complete so second effect doesn't run
        isInitialLoadRef.current = false;
        setCombinedData(mergedData);
        // Dispatch fully loaded action to reducer
        if (stateDispatchFunc) {
          stateDispatchFunc({ type: PropertyShowLoadingActions.MARK_FULLY_LOADED });
        }
      },
      onError: (fetchError, message) => {
        logger.error('Error fetching property data:', fetchError);
        FlashesStore.flash(FlashesStore.ERROR, message || 'Error fetching property data');
        if (stateDispatchFunc) {
          stateDispatchFunc({ type: PropertyShowLoadingActions.SET_ERROR, error: fetchError });
        }
      },
      stateDispatchFunc,
    });
  }, [propertyId, stateDispatchFunc]);

  // Second effect: Fetch time-dependent data when dateSpan changes (using cached static data)
  useEffect(() => {
    if (!propertyId || !dateSpan) {
      logger.error('No property ID or date span found for time-dependent data fetching');
      return;
    }

    // If we don't have static data yet, skip this effect
    if (!staticDataCache.current.hasStaticData) {
      logger.log('Waiting for static data to be cached before fetching time-dependent data');
      return;
    }

    // Skip this effect on initial load - the first effect handles it
    if (isInitialLoadRef.current) {
      logger.log('Skipping second effect on initial load - first effect is handling it');
      return;
    }

    logger.log('Fetching time-dependent data for date range:', dateSpan.start.toISODate(), 'to', dateSpan.finish.toISODate());

    if (stateDispatchFunc) {
      stateDispatchFunc({ type: PropertyShowLoadingActions.START_REQUEST });
    }

    getTimeSeriesData({
      meters: staticDataCache.current.meters,
      rulesData: staticDataCache.current.rulesData,
      dateSpan,
      timezone,
      environment,
      historianAggregation,
      tradeAggregation,
      initialData,
      isFetchingRef: isFetchingData,
      onDataReceived: (mergedData) => {
        setCombinedData(mergedData);
        // Dispatch fully loaded action to reducer
        if (stateDispatchFunc) {
          stateDispatchFunc({ type: PropertyShowLoadingActions.MARK_FULLY_LOADED });
        }
      },
      onError: (fetchError, message) => {
        logger.error('Error fetching time-dependent data:', fetchError);
        FlashesStore.flash(FlashesStore.ERROR, message || 'Error fetching time-dependent data');
        if (stateDispatchFunc) {
          stateDispatchFunc({ type: PropertyShowLoadingActions.SET_ERROR, error: fetchError });
        }
      },
      stateDispatchFunc,
    });
  }, [
    propertyId,
    dateSpan?.start,
    dateSpan?.finish,
    timezone,
    environment,
    historianAggregation,
    tradeAggregation,
    stateDispatchFunc,
  ]);

  return {
    combinedData,
  };
};

export default usePropertyData;
